// Angular imports
import { Component, OnDestroy, OnInit } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';

// Common imports
import { CCLocalStorageCategory, CCLocalStorageService, Utils } from 'cc-common';

// Component imports
import { BaseComponent } from '../../_base/mp-base-component';

// Service imports
import { AppInfoService } from '../../_services/app-info.service';
import { ScreenService } from '../../_services/shared-services/screen.service';
import { HelperService } from '../../_services/shared-services/helper.service';
import { UserService } from '../../_services/data-services/user.service';
import { AuthService } from '../../_services/auth.service';
import { ContextManagerService } from '../../_services/data-services/context-manager.service';

// 3rd party imports
import { Subscription } from 'rxjs';

// Model imports
import { PropertyCommunityInfoResultDto } from '../../_models/property-community-info-result-dto';
import { BrandingConfigurationResultDto } from '../../_models/branding-configuration-result-dto';

// Constant imports
import { ImageConstants } from '../../_constants/image-constants';
import { StorageKeyConstants } from '../../_constants/storage-key-constants';

@Component({
    selector: 'app-branding-image',
    templateUrl: './branding-image.component.html',
    styleUrls: ['./branding-image.component.scss']
})
export class BrandingImageComponent extends BaseComponent implements OnInit, OnDestroy {
    isSmallScreen: boolean;
    logoLocation = 'before';

    logoImage: string = ImageConstants.ccLogo;
    title: string;

    brandImage: SafeUrl;
    brandAltText: string;

    selectedContext: PropertyCommunityInfoResultDto;
    themeColorSubscription: Subscription;

    constructor(
        protected readonly screenService: ScreenService,
        private readonly appInfoService: AppInfoService,
        private readonly authService: AuthService,
        private readonly userService: UserService,
        private readonly helperService: HelperService,
        private readonly contextService: ContextManagerService,
        private readonly localStorageService: CCLocalStorageService
    ) {
        super(screenService);
        this.title = this.appInfoService.title;
    }

    ngOnInit(): void {
        // If any changes in prefrences especially in user-themes, then updating Branding Image
        this.themeColorSubscription = this.userService.hasUpdateInThemeColor().subscribe((changeInThemeColor) => {
            if (Utils.isTrue(changeInThemeColor)) {
                const brandingInformation = this.userService.getBrandingInforamtion();
                if (Utils.notNullAndDefined(brandingInformation)) {
                    this.applyThemeBasedBrandingImage(brandingInformation);
                }
            }
        });
        this.brandImage = null;
        this.brandAltText = null;

        this.updateScreenSize();
        this.screenService.changed.subscribe(() => this.updateScreenSize());

        this.updateContextChange();
        this.contextService.getContext().subscribe(() => this.updateContextChange());
    }

    ngOnDestroy(): void {
        if (Utils.notNullAndDefined(this.themeColorSubscription)) {
            this.themeColorSubscription.unsubscribe();
        }
    }

    private updateContextChange(): void {
        this.selectedContext = this.contextService.getSelectedContext();
        if (
            Utils.notNullAndDefined(this.selectedContext) &&
            Utils.notNullAndDefined(this.selectedContext.communityId)
        ) {
            this.populateBrandingInformation(this.selectedContext.communityId);
        } else {
            this.userService.setBrandingInforamtion(null);
        }
    }

    private updateScreenSize(): void {
        this.isSmallScreen = this.screenService.isSmallScreen();
    }

    private populateBrandingInformation(communityId: number): void {
        this.authService.getBrandingInformation(communityId).subscribe((data: BrandingConfigurationResultDto) => {
            this.userService.setBrandingInforamtion(data);
            if (Utils.notNullAndDefined(data)) {
                this.applyThemeBasedBrandingImage(data);
            }
        });
    }

    private applyThemeBasedBrandingImage(data: BrandingConfigurationResultDto): void {
        const selectedThemeColor = this.localStorageService.getItem(
            StorageKeyConstants.userThemeKey,
            CCLocalStorageCategory.PREF
        );

        let brandingImageUrl = null;
        let taglineText = null;

        if (Utils.notNullAndDefined(data)) {
            brandingImageUrl =
                Utils.notNullOrEmpty(selectedThemeColor) &&
                selectedThemeColor.includes('dark') &&
                Utils.notNullOrEmpty(data.darkBgBrandingImageUrl)
                    ? data.darkBgBrandingImageUrl
                    : data.brandingImageUrl;
            taglineText = Utils.notNullOrEmpty(data.taglineText) ? data.taglineText : null;
        }

        if (Utils.notNullOrEmpty(brandingImageUrl)) {
            this.helperService.getImageSafeUrlFromUrl(brandingImageUrl).subscribe((imageUrl) => {
                this.brandImage = imageUrl;
                this.brandAltText = taglineText;
            });
        } else {
            this.brandImage = null;
            this.brandAltText = null;
        }
    }
}
